// lib/modelGalleryCopy.ts
import { type StudioModelId } from '#lib/model-registry';

type ModelGalleryLanguage = 'en' | 'fr';

type ModelGalleryCopyEntry = {
  reason: string;
  tradeOff?: string;
};

type ModelGalleryCopyI18nEntry = {
  en: ModelGalleryCopyEntry;
  fr: ModelGalleryCopyEntry;
};

const MODEL_GALLERY_COPY: Partial<Record<StudioModelId, ModelGalleryCopyI18nEntry>> = {
  'nano-banana-2': {
    en: {
      reason: 'Flagship baseline for instruction reliability and final quality.',
      tradeOff: 'A bit slower than fast alternatives.',
    },
    fr: {
      reason: 'Modele flagship pour la fiabilite des instructions et la qualite finale.',
      tradeOff: 'Un peu plus lent que les alternatives rapides.',
    },
  },
  'nano-banana-2.1': {
    en: {
      reason: 'Newer NB2 class: sharper output and in-image text, and slightly cheaper than NB2 at 2K.',
      tradeOff: 'Always reasons before drawing, so it can be slower than NB2.',
    },
    fr: {
      reason: "Classe NB2 plus recente : rendu plus net et texte dans l'image, un peu moins cher que NB2 en 2K.",
      tradeOff: 'Raisonne toujours avant de dessiner, donc parfois plus lent que NB2.',
    },
  },
  'grok-imagine-image': {
    en: {
      reason: 'Good quality, faster and cheaper than NB2.',
      tradeOff: 'Does not support additional reference images.',
    },
    fr: {
      reason: 'Bonne qualite, plus rapide et moins cher que NB2.',
      tradeOff: "Ne supporte pas d'images de reference additionnelles.",
    },
  },
  'grok-imagine-2': {
    en: {
      reason: 'Same credit price as NB2 at 1K and 2K.',
      tradeOff: 'Two extra reference images at most, against 14, and no 4K.',
    },
    fr: {
      reason: 'Meme prix en credits que NB2 en 1K et 2K.',
      tradeOff: 'Deux images de reference additionnelles au maximum, contre 14, et pas de 4K.',
    },
  },
  'muse-image': {
    en: {
      reason: '1 credit per image, up to 9 extra reference images.',
      tradeOff: 'Output fixed around 2.5 Mpx, no 4K, speed not measured.',
    },
    fr: {
      reason: "1 credit par image, jusqu'a 9 images de reference additionnelles.",
      tradeOff: 'Sortie fixee autour de 2,5 Mpx, pas de 4K, vitesse non mesuree.',
    },
  },
  'flux-3-image': {
    en: {
      reason: 'Five resolution tiers up to 4K, up to 9 extra reference images.',
      tradeOff: 'No mask, about 2 minutes at 2K, and 4K may take several minutes.',
    },
    fr: {
      reason: "Cinq paliers de resolution jusqu'a la 4K, jusqu'a 9 images de reference additionnelles.",
      tradeOff: 'Pas de masque, environ 2 minutes en 2K, et la 4K peut demander plusieurs minutes.',
    },
  },
  'flux-3-klein': {
    en: {
      reason: 'Open-weights FLUX 3, output sized to the frame up to 4K, up to 9 extra reference images.',
      tradeOff: 'Closer to the brief on shopfronts than on development projects; behind Nano Banana 2.1 on URW edits.',
    },
    fr: {
      reason: "FLUX 3 en poids ouverts, sortie a la taille du cadre jusqu'a la 4K, jusqu'a 9 images de reference additionnelles.",
      tradeOff: 'Plus fidele sur les devantures que sur les projets de developpement ; derriere Nano Banana 2.1 sur les retouches URW.',
    },
  },
  'flux-2-klein-4b': {
    en: {
      reason: 'Ultra-fast and cheaper than NB2 for quick iteration loops.',
      tradeOff: 'Output quality is medium versus NB2.',
    },
    fr: {
      reason: 'Ultra rapide et moins cher que NB2 pour iterer vite.',
      tradeOff: 'Qualite de sortie moyenne face a NB2.',
    },
  },
  'gpt-image-2': {
    en: {
      reason: 'Use it when you want maximum raw visual quality.',
      tradeOff: 'Very slow and often deforms localized edits.',
    },
    fr: {
      reason: 'A utiliser pour obtenir la meilleure qualite visuelle brute.',
      tradeOff: 'Tres lent et deforme souvent les edits localises.',
    },
  },
  'gpt-image-2.5': {
    en: {
      reason: 'High-fidelity fal image generation and full-area editing.',
      tradeOff: 'Quality controls cost 1–2.5 credits; up to 10 reference images, no masks.',
    },
    fr: {
      reason: 'Generation et edition plein cadre haute fidelite via fal.',
      tradeOff: 'La qualite coute 1 a 2,5 credits; au plus 10 images de reference, pas de masque.',
    },
  },
  'mai-2.5-flash': {
    en: {
      reason: 'Fast Azure-hosted image editing model (MAI 2.5 Flash).',
      tradeOff: 'Output is always PNG, max 1,048,576 pixels (1024x1024 equivalent).',
    },
    fr: {
      reason: 'Modele d edition d image rapide heberge sur Azure (MAI 2.5 Flash).',
      tradeOff: 'Format de sortie toujours PNG, limite a 1 048 576 pixels (equiv 1024x1024).',
    },
  },
  'nano-banana-pro': {
    en: {
      reason: 'Legacy-only access path kept for backward compatibility.',
      tradeOff: 'Superseded by Nano Banana 2 for normal workflows.',
    },
    fr: {
      reason: 'Acces legacy uniquement pour compatibilite historique.',
      tradeOff: 'Remplace par Nano Banana 2 pour les workflows normaux.',
    },
  },
  'qwen-image-3-pro': {
    en: {
      reason: 'Best-in-line text rendering and complex layouts; benchmarks above Nano Banana 2.',
      tradeOff: 'Two extra reference images at most; no mask support.',
    },
    fr: {
      reason: 'Meilleur rendu de texte et de mises en page complexes ; au-dessus de Nano Banana 2 aux benchmarks.',
      tradeOff: "Deux images de reference supplementaires au maximum ; pas de masque.",
    },
  },
  'qwen-image-2-pro': {
    en: {
      reason: 'Kept for comparison runs against Qwen Image 3 Pro.',
      tradeOff: 'Superseded by Qwen Image 3 Pro, which does the same job better.',
    },
    fr: {
      reason: 'Conserve pour comparer avec Qwen Image 3 Pro.',
      tradeOff: "Remplace par Qwen Image 3 Pro, qui fait le meme travail en mieux.",
    },
  },
  'seedream4.5': {
    en: {
      reason: 'Experimental fallback to test when NB2 or Grok fail a task.',
      tradeOff: 'Not a primary production baseline.',
    },
    fr: {
      reason: 'Fallback experimental a tester en cas d echec de NB2 ou Grok.',
      tradeOff: "Pas un baseline de production principal.",
    },
  },
  'kling-3': {
    en: {
      reason: 'Reliable cinematic video generation with strong motion and detail quality.',
      tradeOff: 'Slower than fast variants and can cost more at 1080p + audio.',
    },
    fr: {
      reason: 'Generation video cinematique fiable avec bonne qualite de mouvement et de details.',
      tradeOff: 'Plus lent que les variantes rapides et peut couter plus en 1080p + audio.',
    },
  },
  'kling-3-omni': {
    en: {
      reason: 'Best when you need reference-video driven edits and continuity.',
      tradeOff: 'Strict input constraints (duration/resolution/format) and slower runtime.',
    },
    fr: {
      reason: 'Ideal quand vous avez besoin d edits guides par video de reference et de continuite.',
      tradeOff: 'Contraintes d entree strictes (duree/resolution/format) et execution plus lente.',
    },
  },
  'flux-3': {
    en: {
      reason: 'Cheapest premium video: native audio, up to 20s, optional end frame.',
      tradeOff: 'No reference images beyond start/end frame, no seed, capped at 1080p.',
    },
    fr: {
      reason: 'La video premium la moins chere : audio natif, jusqu a 20s, frame de fin optionnelle.',
      tradeOff: 'Pas de references au dela des frames de debut/fin, pas de seed, limite a 1080p.',
    },
  },
  'flux-3-draft': {
    en: {
      reason: 'Fast cheap draft of FLUX 3, to prove a motion before paying for the final.',
      tradeOff: 'Draft quality at 720p; never deliver it as the final render.',
    },
    fr: {
      reason: 'Brouillon rapide et bon marche de FLUX 3, pour valider un mouvement avant le rendu final.',
      tradeOff: 'Qualite brouillon en 720p; ne jamais le livrer comme rendu final.',
    },
  },
  'seedance-2.5': {
    en: {
      reason: 'Newest Seedance: one continuous take up to 30s, strong character and set consistency, native synced audio.',
      tradeOff: 'Capped at 720p, no seed and no end frame; long takes cost more per clip.',
    },
    fr: {
      reason: 'Le Seedance le plus recent : un plan continu jusqu a 30s, forte coherence personnage et decor, audio synchronise natif.',
      tradeOff: 'Limite a 720p, pas de seed ni de frame de fin; les plans longs coutent plus cher.',
    },
  },
  'seedance-2.0': {
    en: {
      reason: 'Flexible reference-to-video model with good balance of speed and quality.',
      tradeOff: 'Quality can vary by prompt/reference strength; high settings increase cost.',
    },
    fr: {
      reason: 'Modele reference-to-video flexible avec bon equilibre vitesse/qualite.',
      tradeOff: 'La qualite varie selon prompt/references; les reglages eleves augmentent le cout.',
    },
  },
  'gemini-omni-flash-preview': {
    en: {
      reason: 'Fast Gemini-native short-video generation with image, video, and audio guidance.',
      tradeOff: 'Preview model focused on short 720p clips; aspect ratio is guided through instructions.',
    },
    fr: {
      reason: 'Generation video courte rapide via Gemini avec guidage image, video et audio.',
      tradeOff: 'Modele preview centre sur des clips courts 720p; le ratio est guide par instruction.',
    },
  },
  'topaz-video-upscale': {
    en: {
      reason: 'Dedicated quality pass for sharpening/upscaling existing videos.',
      tradeOff: 'Not a generation model; best used after content is already finalized.',
    },
    fr: {
      reason: 'Pass qualite dediee pour renforcer/upscaler des videos existantes.',
      tradeOff: 'Ce n est pas un modele de generation; ideal apres finalisation du contenu.',
    },
  },
  'mirelo-sfx-1.6': {
    en: {
      reason: 'Creates frame-synchronized Foley, ambience, impacts, and scene detail from a timeline clip.',
      tradeOff: 'Sound-only workflow for 1 to 60 second source ranges; it does not create dialogue or music.',
    },
    fr: {
      reason: 'Cree des bruitages, ambiances, impacts et details de scene synchronises sur un clip de timeline.',
      tradeOff: 'Workflow sonore pour des plages source de 1 a 60 secondes; il ne cree ni dialogue ni musique.',
    },
  },
  'magnific-video-upscale': {
    en: {
      reason: 'Creative AI upscaler for enhancing existing videos up to 4K.',
      tradeOff: 'May reinterpret fine details when creativity is raised; best for finished clips.',
    },
    fr: {
      reason: 'Upscaler IA creatif pour ameliorer des videos existantes jusqu en 4K.',
      tradeOff: 'Peut reinterpretter les details fins si la creativite augmente; ideal pour les clips finalises.',
    },
  },
  'topaz-image-enhance': {
    en: {
      reason: 'Dedicated quality pass for upscaling and sharpening the current image area.',
      tradeOff: 'Prompt and references are ignored; it enhances existing pixels.',
    },
    fr: {
      reason: 'Pass qualite dediee pour upscaler et renforcer la zone image actuelle.',
      tradeOff: 'Le prompt et les references sont ignores; il ameliore les pixels existants.',
    },
  },
  'magnific-image-upscale': {
    en: {
      reason: 'High-detail Magnific Precision V2 upscaler for final image quality passes.',
      tradeOff: 'Expensive and may synthesize fine detail; not for instruction-driven edits.',
    },
    fr: {
      reason: 'Upscaler Magnific Precision V2 haute definition pour les passes qualite finales.',
      tradeOff: 'Couteux et peut synthetiser des details fins; pas adapte aux edits instructionnels.',
    },
  },
};

export const getModelGalleryCopy = (
  modelId: string,
  lang: ModelGalleryLanguage = 'en',
): ModelGalleryCopyEntry | null => {
  const entry = MODEL_GALLERY_COPY[modelId as StudioModelId];
  if (!entry) return null;
  return lang === 'fr' ? entry.fr : entry.en;
};
